import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Regulatory citations render as one color-coded chip per authority with a
// citation count; hovering opens a popover listing each citation, and citations
// carrying a source URL are clickable. Replaces the stacked text dump.

const AUTHORITY_BADGE_STYLES = {
  ICH: "bg-blue-50 text-blue-700 ring-blue-200",
  CFR: "bg-purple-50 text-purple-700 ring-purple-200",
  FDA: "bg-red-50 text-red-700 ring-red-200",
  EMA: "bg-green-50 text-green-700 ring-green-200",
  EU: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  MHRA: "bg-teal-50 text-teal-700 ring-teal-200",
  PMDA: "bg-rose-50 text-rose-700 ring-rose-200",
  ISO: "bg-cyan-50 text-cyan-700 ring-cyan-200",
  WHO: "bg-sky-50 text-sky-700 ring-sky-200",
  HIPAA: "bg-orange-50 text-orange-700 ring-orange-200",
  default: "bg-gray-50 text-gray-600 ring-gray-200",
};

const AUTHORITY_POPOVER_STYLES = {
  ICH: "bg-blue-50 ring-blue-200",
  CFR: "bg-purple-50 ring-purple-200",
  FDA: "bg-red-50 ring-red-200",
  EMA: "bg-green-50 ring-green-200",
  EU: "bg-emerald-50 ring-emerald-200",
  MHRA: "bg-teal-50 ring-teal-200",
  PMDA: "bg-rose-50 ring-rose-200",
  ISO: "bg-cyan-50 ring-cyan-200",
  WHO: "bg-sky-50 ring-sky-200",
  HIPAA: "bg-orange-50 ring-orange-200",
  default: "bg-gray-50 ring-gray-200",
};

const AUTHORITY_LINK_STYLES = {
  ICH: "text-blue-800 hover:text-blue-950 hover:bg-blue-100",
  CFR: "text-purple-800 hover:text-purple-950 hover:bg-purple-100",
  FDA: "text-red-800 hover:text-red-950 hover:bg-red-100",
  EMA: "text-green-800 hover:text-green-950 hover:bg-green-100",
  EU: "text-emerald-800 hover:text-emerald-950 hover:bg-emerald-100",
  MHRA: "text-teal-800 hover:text-teal-950 hover:bg-teal-100",
  PMDA: "text-rose-800 hover:text-rose-950 hover:bg-rose-100",
  ISO: "text-cyan-800 hover:text-cyan-950 hover:bg-cyan-100",
  WHO: "text-sky-800 hover:text-sky-950 hover:bg-sky-100",
  HIPAA: "text-orange-800 hover:text-orange-950 hover:bg-orange-100",
  default: "text-gray-700 hover:text-gray-900 hover:bg-gray-100",
};

const CATCHALL_AUTHORITY = "Additional Regulations & Guidance";

const getStyle = (title, map) => {
  if (!title) return map.default;
  const upper = title.toUpperCase();
  for (const [key, val] of Object.entries(map)) {
    if (key !== "default" && upper.includes(key)) return val;
  }
  return map.default;
};

// One citation string may carry a source URL; strip it from the label and make
// the citation a link. Splitting is on semicolons only — citations themselves
// legitimately contain commas ("21 CFR Part 11, Subpart B").
export const parseCitations = (data) =>
  String(data ?? "")
    .split(";")
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((raw) => {
      const urlMatch = raw.match(/https?:\/\/\S+/);
      if (!urlMatch) return { label: raw, url: null };
      const url = urlMatch[0].replace(/[.,;)]+$/, "");
      const label = raw.replace(urlMatch[0], "").trim().replace(/[,;]+$/, "") || url;
      return { label, url };
    });

export const groupCitationsByAuthority = (citations) => {
  const groups = new Map();
  for (const c of Array.isArray(citations) ? citations : []) {
    const title = (c?.title ?? "").trim();
    const data = (c?.data ?? "").trim();
    if (!title || !data) continue;
    const display = title.toLowerCase() === CATCHALL_AUTHORITY.toLowerCase() ? "Other" : title;
    if (!groups.has(display)) groups.set(display, []);
    groups.get(display).push(...parseCitations(data));
  }
  return Array.from(groups, ([authority, items]) => ({ authority, items }));
};

const AuthorityChip = ({ group, isOpen, onOpen, onClose }) => {
  const badgeStyle = getStyle(group.authority, AUTHORITY_BADGE_STYLES);
  const popoverStyle = getStyle(group.authority, AUTHORITY_POPOVER_STYLES);
  const linkStyle = getStyle(group.authority, AUTHORITY_LINK_STYLES);

  return (
    <span className="relative inline-flex" onMouseEnter={onOpen} onMouseLeave={onClose}>
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset cursor-default ${badgeStyle}`}
      >
        {group.authority}
        <span className="text-[9px] font-normal opacity-50">{group.items.length}</span>
      </span>

      {isOpen ? (
        <div
          className={`absolute left-0 top-full mt-1 z-30 rounded-lg px-2 py-1.5 ring-1 ring-inset shadow-lg min-w-[140px] max-w-[320px] ${popoverStyle}`}
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
        >
          <div className="flex flex-wrap gap-1">
            {group.items.map((item, i) =>
              item.url ? (
                <a
                  key={i}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`px-1.5 py-0.5 rounded text-[11px] font-medium underline-offset-2 hover:underline ${linkStyle}`}
                >
                  {item.label}
                </a>
              ) : (
                <span key={i} className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${linkStyle}`}>
                  {item.label}
                </span>
              )
            )}
          </div>
        </div>
      ) : null}
    </span>
  );
};

const QuestionCitations = ({ citations }) => {
  const grouped = useMemo(() => groupCitationsByAuthority(citations), [citations]);
  const [openIdx, setOpenIdx] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleOpen = useCallback((idx) => {
    clearTimeout(closeTimer.current);
    setOpenIdx(idx);
  }, []);

  const handleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenIdx(null), 120);
  }, []);

  if (grouped.length === 0) return null;

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
        Regulatory &amp; compliance standards
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {grouped.map((group, idx) => (
          <AuthorityChip
            key={group.authority}
            group={group}
            isOpen={openIdx === idx}
            onOpen={() => handleOpen(idx)}
            onClose={handleClose}
          />
        ))}
      </div>
    </div>
  );
};

export default QuestionCitations;
